import React, { useState } from 'react';
import {
  Button,
  Icon,
  Input,
  PopConfirm,
  Popover,
  Select,
  UploadWrapper,
  UserSearch,
} from '../../../../components';
import MemberOptionItem from '../../../../components/MemberOptionItem';
import TagPicker from '../../../../components/TagPicker';
import { getTemptId, mapFileData } from '../../../../utils/helpers';
import { toast } from 'react-toastify';
import { DatePicker, Radio, Switch } from 'antd';
import { moment } from '../../../../utils/moment';
import { useDispatch, useSelector } from 'react-redux';
import { FieldTimeOutlined } from '@ant-design/icons';
import {
  archiveTask,
  createTaskAttachment,
  createTodoGroup,
  deleteTask,
  duplicateBoard,
  unarchiveTask,
  updateTask,
} from '../../actions';
import { createNotification } from '../../../Dashboard/actions';
import { useEffect } from 'react';

const boards = [
  { value: '1', label: 'Todo' },
  { value: '2', label: 'On Progress' },
  { value: '3', label: 'On Review' },
  { value: '4', label: 'Done' },
];

const ActionList = ({ task, project, setBoards, closeModal, updateData }) => {
  const [todoTitle, setTodoTitle] = React.useState('');
  const [dateVisible, setDateVisible] = React.useState(false);
  const [name, setName] = React.useState('');

  const [automationEnabled, setAutomationEnabled] = React.useState(
    task.automation.enabled
  );
  const [mode, setMode] = useState(task.automation.mode);

  const userId = useSelector((s) => s.auth.user.data._id);
  const dispatch = useDispatch();

  const changeBoard = React.useCallback(
    (newBoard) => {
      setBoards((s) => {
        const newIndex = s[newBoard].length;
        dispatch(updateTask({ data: { board: newBoard, index: newIndex } }));
        return {
          ...s,
          [task.board]: s[task.board].filter((t) => t._id !== task._id),
          [newBoard]: [...s[newBoard], { ...task, board: newBoard }],
        };
      });
    },
    [task]
  );

  const duplicate = React.useCallback(() => {
    const newItem = { ...task, _id: getTemptId() };
    setBoards((s) => ({ ...s, [task.board]: [...s[task.board], newItem] }));
    dispatch(duplicateBoard(newItem));
    toast.success('Activity duplicated');
  }, [task]);

  const archive = React.useCallback(() => {
    setBoards((s) => ({
      ...s,
      [task.board]: s[task.board].filter((t) => t._id !== task._id),
    }));
    dispatch(archiveTask([task._id]));
    closeModal();
    toast.success('Activity archived');
  }, [task]);
  const unarchive = React.useCallback(() => {
    dispatch(unarchiveTask([task._id]));
  }, [task]);

  const removeTask = React.useCallback(() => {
    setBoards((s) => ({
      ...s,
      [task.board]: s[task.board].filter((t) => t._id !== task._id),
    }));
    dispatch(deleteTask([task._id]));
    closeModal();
    toast.success('Activity deleted');
  }, [task]);

  const addAttachment = React.useCallback(
    (files) => {
      const newFiles = files.map((file) => ({
        ...mapFileData(file),
        createdAt: new Date(),
      }));
      dispatch(
        createTaskAttachment({
          data: newFiles,
          finalData: files,
          task: task._id,
        })
      );
    },
    [task._id]
  );

  const updateEndDate = React.useCallback((date) => {
    updateData(() => ({ endDate: moment(date).format() }));
  }, []);

  const addTodo = React.useCallback(() => {
    if (todoTitle) {
      const newItem = { _id: getTemptId(), title: todoTitle, list: [] };
      dispatch(createTodoGroup(newItem));
      setTodoTitle('');
    }
  }, [todoTitle]);

  const addMember = React.useCallback(
    (item) => {
      const userItem = project.members
        .map((m) => m.user)
        .find((u) => u._id === item.value);
      const finalData = {
        members: [...(task.members?.map((m) => m._id) || []), item.value],
      };
      updateData(
        (s) => ({ members: [...(s.members || []), userItem] }),
        finalData
      );
      if (userId !== item.value && !project.mutedBy?.includes(item.value))
        dispatch(
          createNotification({
            user: item.value,
            title: `You have been assigned to Activity "${task.title.substring(
              0,
              10
            )}"`,
          })
        );
    },
    [task.members, userId, project.mutedBy]
  );

  const removeMember = React.useCallback(
    (id) => {
      const finalData = {
        members: task.members?.filter((m) => m._id !== id).map((m) => m._id),
      };
      updateData(
        (s) => ({ members: s.members?.filter((m) => m._id !== id) }),
        finalData
      );
    },
    [task.members]
  );

  const handleRadioAutomation = (e) => {
    setMode(e.target.value);
  };

  useEffect(() => {
    console.log('automation', automationEnabled);
    console.log('enabled mode', mode);
    let finalData = {
      automation: { enabled: automationEnabled, mode: mode },
    };
    updateData(() => finalData);
  }, [automationEnabled, mode]);

  const handleAutomationDateAlert = () => {
    toast.warn('Please choose a date for automation.');
    setDateVisible(true);
  };

  const handleAutomation = () => {
    if (task.endDate) {
      setAutomationEnabled(!automationEnabled);
    } else {
      handleAutomationDateAlert();
    }
  };

  const automationIcon = {
    fontSize: '18px',
    marginRight: '3px',
  };

  return (
    <div className="actionList">
      <Popover
        title="Members"
        button={
          <div className="item">
            <Icon name="user" /> Members
          </div>
        }
        content={
          <div className="popoverContent">
            <UserSearch
              addedItems={task.members}
              onSelect={(val) => addMember(val)}
              value={name}
              onChange={setName}
              task
            />
            {task.members?.map((item) => (
              <MemberOptionItem
                key={item._id}
                item={item}
                onDelete={() => removeMember(item._id)}
              />
            ))}
          </div>
        }
      />
      <Popover
        title="Tags"
        button={
          <div className="item">
            <Icon name="tag" /> Tags
          </div>
        }
        content={
          <div className="popoverContent tagPopover">
            <TagPicker
              tags={task.tags}
              onChange={(tags) => updateData(() => ({ tags }))}
            />
          </div>
        }
      />
      <div className="datePicker">
        <DatePicker
          open={dateVisible}
          showTime={{ format: 'HH:mm' }}
          format="YYYY-MM-DD HH:mm"
          onChange={updateEndDate}
          defaultValue={moment(task.endDate)}
          onOk={() => setDateVisible(false)}
        />
      </div>
      <div className="item" onClick={() => setDateVisible(!dateVisible)}>
        <Icon name="time" /> Date
      </div>
      <UploadWrapper onChange={addAttachment} multiple>
        <div className="item">
          <Icon name="attachment" /> Attachment
        </div>
      </UploadWrapper>
      <Popover
        title="Todo List"
        button={
          <div className="item">
            <Icon name="todo" />
            Todo List
          </div>
        }
        content={
          <div className="popoverContent">
            <Input
              placeholder="Todo Title"
              value={todoTitle}
              onChange={setTodoTitle}
              autoFocus
              onKeyDown={(e) => e.key === 'Enter' && addTodo()}
            />
            <Button title="Add List" onClick={addTodo} />
          </div>
        }
      />
      <br />
      <Popover
        title="Move to"
        button={
          <div className="item">
            <Icon name="arrow-right" /> Move to
          </div>
        }
        content={
          <div className="popoverContent">
            <Select
              placeholder="Board"
              value={task.board.toString()}
              options={boards}
              onChange={changeBoard}
            />
          </div>
        }
      />
      <div className="item" onClick={duplicate}>
        <Icon name="duplicate" /> Duplicate
      </div>
      {!task.archived ? (
        <PopConfirm
          title="Are you sure to archive this Activity?"
          onConfirm={archive}
          content={
            <div className="item">
              <Icon name="archive" /> Archive
            </div>
          }
        />
      ) : (
        <div onClick={unarchive} className="item">
          <Icon name="rotate" /> Unarchive
        </div>
      )}
      <div
        className="item"
        style={{ minHeight: '50px', flexDirection: 'column' }}
        disabled={!task.endDate ? true : false}
        // onClick={() => handleDisable}
      >
        <div
          style={{
            display: 'flex',
            flexDirection: 'row',
            justifyContent: 'flex-start',
            alignItems: 'center',
            width: '100%',
          }}
        >
          <FieldTimeOutlined style={automationIcon} />
          <span style={{ marginRight: '10px' }}>Automation</span>
          <Switch
            size="small"
            checked={task.automation.enabled}
            onChange={() => handleAutomation()}
            // disabled={!task.endDate ? true : false}
          />
        </div>
        <div style={{ flexDirection: 'row', fontSize: '10px', color: 'grey' }}>
          <Radio.Group
            onChange={handleRadioAutomation}
            value={mode}
            disabled={
              (!automationEnabled ? true : false) ||
              (!task.endDate ? true : false)
            }
          >
            <Radio value={'daily'} style={{ color: 'grey', fontSize: '12px' }}>
              Daily
            </Radio>
            <Radio value={'weekly'} style={{ color: 'grey', fontSize: '12px' }}>
              Weekly
            </Radio>
          </Radio.Group>
        </div>
      </div>

      <PopConfirm
        title="Are you sure to delete this Activity?"
        onConfirm={removeTask}
        content={
          <div className="item danger">
            <Icon name="delete" /> Delete
          </div>
        }
      />
    </div>
  );
};

export default ActionList;
